import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { SafetyDaysConfig } from '../lib/replenishment'

/**
 * Anti-ruptura (Fase 1): dias de cobertura de segurança por curva ABC da empresa
 * ativa. Somados ao lead time no ponto de ressuprimento (ver computeReplenishment)
 * para que cada compra restaure um colchão e o item não zere (status roxo).
 *
 * Mesmo padrão de useCompanyBudget: lê uma vez no mount, expõe `update` que grava
 * em companies.* e dispara `REPLENISHMENT_REFRESH_EVENT` para o Painel BX/Sugestão
 * recomputarem com o novo colchão sem precisar recarregar a página.
 */

/** Evento de janela para forçar refetch do motor de reposição após editar a config. */
export const REPLENISHMENT_REFRESH_EVENT = 'bx:replenishment-refresh'

const DEFAULT_SAFETY: SafetyDaysConfig = { a: 10, b: 7, c: 4 }

export function useCompanySafetyStock() {
  const [safety, setSafety] = useState<SafetyDaysConfig>(DEFAULT_SAFETY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { if (!cancelled) setLoading(false); return }
      const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
      if (!profile?.company_id) { if (!cancelled) setLoading(false); return }
      const { data, error: err } = await supabase
        .from('companies')
        .select('safety_days_a, safety_days_b, safety_days_c')
        .eq('id', profile.company_id)
        .single()
      if (cancelled) return
      if (err) { setError(err.message); setLoading(false); return }
      setSafety({
        a: Number(data?.safety_days_a ?? DEFAULT_SAFETY.a),
        b: Number(data?.safety_days_b ?? DEFAULT_SAFETY.b),
        c: Number(data?.safety_days_c ?? DEFAULT_SAFETY.c),
      })
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  const update = useCallback(async (patch: Partial<SafetyDaysConfig>): Promise<boolean> => {
    setSaving(true)
    setError(null)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setSaving(false); return false }
    const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
    if (!profile?.company_id) { setSaving(false); return false }
    const next: SafetyDaysConfig = { ...safety, ...patch }
    const { error: err } = await supabase
      .from('companies')
      .update({ safety_days_a: next.a, safety_days_b: next.b, safety_days_c: next.c })
      .eq('id', profile.company_id)
    setSaving(false)
    if (err) { setError(err.message); return false }
    setSafety(next)
    // Avisa o motor de reposição (useReplenishment) para recomputar com o novo colchão.
    window.dispatchEvent(new Event(REPLENISHMENT_REFRESH_EVENT))
    return true
  }, [safety])

  return { safety, loading, error, saving, update }
}
